import {
  PageHeader,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { Plus, Trash2 } from "@/components/icons";
import { deleteTemplate, listTemplates, setDefaultTemplate } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { buildTemplateEditPath, TEMPLATE_NEW_PATH } from "@/lib/templates";
import type { ListTemplatesResp, Template } from "@/lib/types";

/** Palette keys shown as the card summary swatches. */
const SUMMARY_KEYS = [
  "background",
  "primary",
  "secondary",
  "success",
  "warning",
  "error",
];

export function TemplatesListPage() {
  const { t } = useTranslation();
  const query = useQuery<ListTemplatesResp>({
    queryKey: ["templates"],
    queryFn: listTemplates,
  });

  const templates = query.data?.templates ?? [];
  const defaultTemplateId = query.data?.defaultTemplateId;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("templates.eyebrow")}
        title={t("templates.title")}
        subtitle={t("templates.subtitle")}
        actions={
          <Button asChild>
            <Link to={TEMPLATE_NEW_PATH}>
              <Plus className="h-4 w-4" />
              {t("templates.newTemplate")}
            </Link>
          </Button>
        }
      />

      {query.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      )}

      {query.isError && (
        <div className="flex items-center justify-between gap-4 rounded-md border p-4 text-sm">
          <span className="text-destructive">{t("templates.loadFailed")}</span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      )}

      {query.data && templates.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed py-16 text-center">
          <p className="text-sm text-muted-foreground">{t("templates.empty")}</p>
          <Button asChild>
            <Link to={TEMPLATE_NEW_PATH}>{t("templates.createFirst")}</Link>
          </Button>
        </div>
      )}

      {templates.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isDefault={template.id === defaultTemplateId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  isDefault,
}: {
  template: Template;
  isDefault: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => deleteTemplate({ id: template.id }),
    onSuccess: async () => {
      toast.success(t("templates.deleted", { name: template.name }));
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const setDefault = useMutation({
    mutationFn: () =>
      setDefaultTemplate({ id: isDefault ? null : template.id }),
    onSuccess: async () => {
      toast.success(
        isDefault
          ? t("templates.clearedDefault", { name: template.name })
          : t("templates.setDefaultToast", { name: template.name }),
      );
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
          {isDefault && <Badge variant="secondary">{t("templates.defaultBadge")}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="flex flex-wrap gap-1.5">
          {SUMMARY_KEYS.map((key) => (
            <span
              key={key}
              title={`${key}: ${template.palette[key] ?? "-"}`}
              className="h-7 w-7 rounded-md border border-border"
              style={{ backgroundColor: template.palette[key] ?? "#000000" }}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          <span className="text-foreground">Aa</span> {template.fontScheme.heading.family}
          {", "}
          {template.fontScheme.body.family}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={buildTemplateEditPath(template.id)}>{t("common.edit")}</Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={setDefault.isPending}
              onClick={() => setDefault.mutate()}
            >
              {isDefault ? t("templates.clearDefault") : t("templates.setAsDefault")}
            </Button>
          </div>
          <ConfirmDestructiveDialog
            trigger={
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            }
            title={t("templates.deleteDialogTitle", { name: template.name })}
            description={
              <span>{t("templates.deleteDialogDesc")}</span>
            }
            confirmWord={template.name}
            onConfirm={async () => {
              await remove.mutateAsync();
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
